import React, { useContext, useEffect, useRef, useState } from "react";
import "../../styles/styles.css";
import ViewportContext from "../../../../context/studyviewer/ViewportContext";
import * as cornerstone from "@cornerstonejs/core";
import { windowLevel } from "@cornerstonejs/core/utilities";
import {
  load3DViewport,
  disable3DViewport,
  getInitialZoomFor3D,
  syncPrimaryToolTo3DViewport,
} from "../../helpers/CornerstoneViewportService";
import {
  VOLUME_3D_VIEWPORT_ID,
  VOLUME_3D_VIEWPORT_INDEX,
} from "../../helpers/MPRViewportInput";
import themeContext from "../../../../context/theme/ThemeContext";
import Spinner from "../../../../components/Spinner/Spinner";
import { useTranslation } from "react-i18next";
import PatientDetails from "../Overlays/PatientDetails";
import StudyDetails from "../Overlays/StudyDetails";
import DICOMDetails from "../Overlays/DICOMDetails";

const PRESET_LIST = [
  { label: "CT AAA", name: "CT-AAA" },
  { label: "CT AAA 2", name: "CT-AAA2" },
  { label: "CT Air", name: "CT-Air" },
  { label: "CT Bone", name: "CT-Bone" },
  { label: "CT Bones", name: "CT-Bones" },
  { label: "CT Cardiac", name: "CT-Cardiac" },
  { label: "CT Cardiac 2", name: "CT-Cardiac2" },
  { label: "CT Cardiac 3", name: "CT-Cardiac3" },
  { label: "CT Chest Contrast Enhanced", name: "CT-Chest-Contrast-Enhanced" },
  { label: "CT Chest Vessels", name: "CT-Chest-Vessels" },
  { label: "CT Coronary Arteries", name: "CT-Coronary-Arteries" },
  { label: "CT Coronary Arteries 2", name: "CT-Coronary-Arteries-2" },
  { label: "CT Coronary Arteries 3", name: "CT-Coronary-Arteries-3" },
  { label: "CT Cropped Volume Bone", name: "CT-Cropped-Volume-Bone" },
  { label: "CT Fat", name: "CT-Fat" },
  { label: "CT Liver Vasculature", name: "CT-Liver-Vasculature" },
  { label: "CT Lung", name: "CT-Lung" },
  { label: "CT MIP", name: "CT-MIP" },
  { label: "CT Muscle", name: "CT-Muscle" },
  { label: "CT Pulmonary Arteries", name: "CT-Pulmonary-Arteries" },
  { label: "CT Soft Tissue", name: "CT-Soft-Tissue" },
  { label: "MR Angio", name: "MR-Angio" },
  { label: "MR Default", name: "MR-Default" },
  { label: "MR MIP", name: "MR-MIP" },
  { label: "MR T2 Brain", name: "MR-T2-Brain" },
  { label: "DTI FA Brain", name: "DTI-FA-Brain" },
];

const Volume3DViewport = () => {
  const {
    is3DActive,
    volumeId,
    activeMPRViewportIndex,
    setActiveMPRViewportIndex,
    showOverlay,
    mprSeriesData,
    activeMPRTool,
  } = useContext(ViewportContext);
  const { getThemeColor } = useContext(themeContext);

  const { t } = useTranslation();
  const [selectedPreset, setSelectedPreset] = useState("CT-Bone");
  const [showPresetDropdown, setShowPresetDropdown] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [scale, setScale] = useState(1);
  const [voi, setVoi] = useState({ windowCenter: null, windowWidth: null });
  const presetDropdownRef = useRef(null);
  const selectedPresetLabel =
    PRESET_LIST.find((p) => p.name === selectedPreset)?.label ??
    selectedPreset;
  const has3DLoaded = useRef(false);
  const cameraListenerRef = useRef(null);
  const voiListenerRef = useRef(null);

  useEffect(() => {
    if (is3DActive && volumeId && !has3DLoaded.current) {
      has3DLoaded.current = true;
      setIsLoading(true);
      const timer = setTimeout(async () => {
        try {
          await load3DViewport(volumeId);

          const renderingEngine = cornerstone.getRenderingEngine(
            import.meta.env.VITE_RENDERING_ENGINE_ID
          );
          const viewport3D = renderingEngine?.getViewport(VOLUME_3D_VIEWPORT_ID);
          if (viewport3D) {
            syncPrimaryToolTo3DViewport(activeMPRTool);
            getInitialZoomFor3D(setScale);

            // Re-reads the live VOI off the actor's transfer function rather
            // than trusting event payloads — Reset/preset changes go through
            // setPreset(), which rebuilds the transfer function but fires
            // PRESET_MODIFIED (no `range` in its detail), not VOI_MODIFIED.
            const readVoi = () => {
              const props = viewport3D.getProperties(volumeId);
              if (!props?.voiRange) return;
              const { lower, upper } = props.voiRange;
              const { windowWidth, windowCenter } = windowLevel.toWindowLevel(
                lower,
                upper
              );
              setVoi({ windowCenter, windowWidth });
            };

            readVoi();

            const updateZoom = (event) => getInitialZoomFor3D(setScale, event);
            viewport3D.element.addEventListener(
              cornerstone.Enums.Events.CAMERA_MODIFIED,
              updateZoom
            );
            cameraListenerRef.current = {
              element: viewport3D.element,
              handler: updateZoom,
            };

            // Keep the L/W overlay in sync with WindowLevelTool drags
            // (VOI_MODIFIED) and preset changes / Reset (PRESET_MODIFIED) on
            // the 3D pane — otherwise it's stuck showing whatever VOI was
            // read at load time.
            viewport3D.element.addEventListener(
              cornerstone.Enums.Events.VOI_MODIFIED,
              readVoi
            );
            viewport3D.element.addEventListener(
              cornerstone.Enums.Events.PRESET_MODIFIED,
              readVoi
            );
            voiListenerRef.current = {
              element: viewport3D.element,
              handler: readVoi,
            };
          }
        } catch (e) {
          console.warn("Error loading 3D viewport:", e);
        } finally {
          setIsLoading(false);
        }
      }, 500);
      return () => {
        clearTimeout(timer);
        setIsLoading(false);
      };
    }
    if (!is3DActive) {
      has3DLoaded.current = false;
      setIsLoading(false);
    }
  }, [is3DActive, volumeId]);

  useEffect(() => {
    return () => {
      if (cameraListenerRef.current) {
        const { element, handler } = cameraListenerRef.current;
        element.removeEventListener(
          cornerstone.Enums.Events.CAMERA_MODIFIED,
          handler
        );
        cameraListenerRef.current = null;
      }
      if (voiListenerRef.current) {
        const { element, handler } = voiListenerRef.current;
        element.removeEventListener(
          cornerstone.Enums.Events.VOI_MODIFIED,
          handler
        );
        element.removeEventListener(
          cornerstone.Enums.Events.PRESET_MODIFIED,
          handler
        );
        voiListenerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        presetDropdownRef.current &&
        !presetDropdownRef.current.contains(event.target)
      ) {
        setShowPresetDropdown(false);
      }
    };
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  const handlePresetChange = async (presetName) => {
    setSelectedPreset(presetName);
    setShowPresetDropdown(false);

    try {
      const renderingEngine = cornerstone.getRenderingEngine(
        import.meta.env.VITE_RENDERING_ENGINE_ID
      );
      const viewport3D = renderingEngine.getViewport(VOLUME_3D_VIEWPORT_ID);
      if (!viewport3D) return;

      const presets = cornerstone.CONSTANTS.VIEWPORT_PRESETS;
      const preset = presets.find((p) => p.name === presetName);
      if (preset) {
        viewport3D.setProperties({ preset });
        viewport3D.render();
      }
    } catch (e) {
      console.warn("Error applying 3D preset:", e);
    }
  };

  return (
    <div
      id="viewport_3d"
      className="viewport-box"
      onClick={() => setActiveMPRViewportIndex(VOLUME_3D_VIEWPORT_INDEX)}
      style={{ height: "100%", width: "100%", position: "relative" }}
    >
      <div
        id="viewportEntry"
        className="viewport-canvas"
        style={{ height: "100%", width: "100%" }}
      />

      {showOverlay && (
        <div className="viewport-overlay-top-container">
          <PatientDetails viewportData={mprSeriesData} />
          <div className="viewport-overlay-right-column">
            <StudyDetails viewportData={mprSeriesData} isMPR={true} />
          </div>
        </div>
      )}

      {/* 3D controls overlay: preset selector (Rotate/Reset moved to the MPR toolbar) */}
      <div
        style={{
          position: "absolute",
          top: "45px",
          right: "8px",
          zIndex: 10,
          display: "flex",
          gap: "4px",
          alignItems: "center",
        }}
        ref={presetDropdownRef}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowPresetDropdown(!showPresetDropdown);
          }}
          style={{
            backgroundColor: "rgba(0,0,0,0.6)",
            color: "#fff",
            border: "1px solid rgba(255,255,255,0.3)",
            borderRadius: "4px",
            padding: "4px 8px",
            cursor: "pointer",
            fontSize: "11px",
          }}
        >
          {selectedPresetLabel} ▾
        </button>

        {showPresetDropdown && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              right: 0,
              backgroundColor: "#201F1E",
              border: "1px solid rgba(255,255,255,0.2)",
              borderRadius: "4px",
              maxHeight: "300px",
              overflowY: "auto",
              zIndex: 999,
              minWidth: "180px",
            }}
          >
            {PRESET_LIST.map(({ label, name }) => (
              <div
                key={name}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePresetChange(name);
                }}
                style={{
                  padding: "6px 10px",
                  cursor: "pointer",
                  fontSize: "11px",
                  color: selectedPreset === name ? "#4da6ff" : "#fff",
                  backgroundColor:
                    selectedPreset === name
                      ? "rgba(77,166,255,0.15)"
                      : "transparent",
                }}
                onMouseEnter={(e) =>
                  (e.target.style.backgroundColor = "rgba(255,255,255,0.1)")
                }
                onMouseLeave={(e) =>
                  (e.target.style.backgroundColor =
                    selectedPreset === name
                      ? "rgba(77,166,255,0.15)"
                      : "transparent")
                }
              >
                {label}
              </div>
            ))}
          </div>
        )}
      </div>

      {showOverlay && (
        <>
          <div className="viewport-overlay-series">
            <div>
              <span className="viewport-overlay-key">
                {t("viewer.overlay.series")}:{" "}
              </span>
              <span className="viewport-overlay-value">
                {mprSeriesData?.series},{" "}
              </span>
              <span className="viewport-overlay-key">
                {t("viewer.overlay.modality")}:{" "}
              </span>
              <span className="viewport-overlay-value">
                {mprSeriesData?.modality}
              </span>
            </div>
          </div>
          <DICOMDetails voi={voi} scale={scale} orientation="3D" />
        </>
      )}

      {/* Loading spinner */}
      {isLoading && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
            zIndex: 99999,
            position: "absolute",
            top: 0,
          }}
        >
          <Spinner size={15} />
        </div>
      )}
    </div>
  );
};

export default Volume3DViewport;
